/**
 * Carries a shared-chat link across sign-in.
 *
 * A signed-out colleague who opens `/new-ai/shared#<token>` is sent to `/` and
 * then through an MSAL redirect, which keeps neither router state nor the URL
 * fragment, so the token would be lost and they would land on `/ai`. The link
 * is parked in sessionStorage (this tab only) and restored once signed in.
 */

export const SHARED_LINK_LOGIN_KEY = "me8eon:post-login-target";

const SHARED_PATH = "/new-ai/shared";

/** A same-origin shared-chat path with a non-empty fragment, and nothing else. */
function isSharedLinkTarget(value: string): boolean {
    return value.startsWith(`${SHARED_PATH}#`) && value.length > SHARED_PATH.length + 1;
}

export function rememberSharedLinkForLogin(pathname: string, hash: string): void {
    const target = `${pathname}${hash}`;
    if (!isSharedLinkTarget(target)) return;

    try {
        sessionStorage.setItem(SHARED_LINK_LOGIN_KEY, target);
    } catch {
        // Storage can be unavailable (private mode, blocked site data); the
        // user then lands on the default page and can reopen the link.
    }
}

/**
 * Returns the parked link once and forgets it. Anything that is not a
 * shared-chat path is discarded, so this can never become an open redirect.
 */
export function takeSharedLinkAfterLogin(): string | undefined {
    try {
        const target = sessionStorage.getItem(SHARED_LINK_LOGIN_KEY);
        sessionStorage.removeItem(SHARED_LINK_LOGIN_KEY);
        return target !== null && isSharedLinkTarget(target) ? target : undefined;
    } catch {
        return undefined;
    }
}
